import { BCMSRecord, BCMSStateManagerCRUD, Branding, StateManager, useBCMSContext, useModel, useParams, useRoute, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import PaymentView from "components/shared/views/BaseRowView/components/PaymentView";
import { CardOnFileMerchant, Merchant, Transaction } from "model";
import React from "react";
import { SVGAsset } from "res";
import "./styles.css";

/**
 * @function CardOnFileView
 * @return {React.Component}
 * @exported true
 * @description The main component for CardOnFileView
 */
export default function CardOnFileView(props) {
  const globalController = useBCMSContext();
  BCMSRecord.globalControllerFor([ CardOnFileMerchant ], globalController);
  // Get the card id from the parameter
  const data = { ...getDefaultData(), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };

  const controller = (state, setState) => ({
    data: state.data,
    ui: state.ui,

    toggleUIBanner: type => () => {
      const crud = BCMSStateManagerCRUD.init(state);
      crud
        .update([ "ui", type ], !ui.showEasilyUpdateMerchantsBanner)
        .submit(setState);
    }
  });

  return (
    <StateManager state={ { data, ui } } controller={ controller }>
      <PageContent/>
    </StateManager>
  );
}

export const getDefaultData = () => {
  const cardOnFileMerchants = CardOnFileMerchant.initAll()
  const transactions = Transaction.initAll();
  const merchants = Merchant.initAll();

  return {
    cardOnFileMerchants,
    merchants,
    transactions
  };
}

export const getDefaultUI = () => ({
  // If the correct branding is set, then this banner can be shown/hidden
  // in a capture component, for example.
  showEasilyUpdateMerchantsBanner: true
});

function PageContent(props) {
  const { data, ui, toggleUIBanner } = useStateManager();
  const { branding } = useModel();
  const options = branding.options ?? {};
  const hasAddToMerchant = options.push_to_merchant_chw23 === "true";
  
  const defaultCardID = options.include_debit_chw23  !== "false" ? "0" : "1";
  const cardID = useParams().cardID ?? defaultCardID;
  const navigate = useRoute();
  const { goStep, nextStep, prevStep, state } = useWorkflow() ?? {};

  const paymentClick = (merchant, cardOnFileMerchant) => () => {
    goStep(1, {  data: { ...data, 
      merchantID: merchant.id, 
      cardOnFileMerchant, 
      cardOnFileSelectedTabType: "cardonfile" 
    }, ui });
    //evt => navigate(`/home/merchantdetails/${ cardID }/${ transaction.merchant.id }/cardonfile`)
  }

  // Get all transactions with the same card id and the purchase location equal to "card on file"
  // Creating an array of "merchant" objects that matches the logic of the
  // CH21 code in order for the merchants to appear in the same
  // order in both demos.
  // IMPORTANT: The order of the merchants in the "model.xml" is the tie breaker between 2 transactions
  // with the same date. If "Big Box Inc." appears before "FuelStop",
  // then that will be the implicit deciding factor as to which transaction is shown first here.
  // In that case, "Big Box Inc." would be shown first, then "FuelStop".
  const merchantsWithTransaction = data.cardOnFileMerchants
  .reduce((acc, cofMechant) => {
    if(cofMechant.card) {
      if(cofMechant.card.id === cardID) acc.push(cofMechant);
    }
    return acc;
  },[])
  .reduce((acc, cofMechant) => {
    const transaction = data.transactions
    .sort((a, b) => {
      if(a.purchaseDate.isBefore(b.purchaseDate)) { return 1; }
      else if(a.purchaseDate.isAfter(b.purchaseDate)) { return -1; }
      else { return 0; }
    })
    .filter(item => {
      return item.card.id === cardID
      && item.merchant.id.toLowerCase() === cofMechant.merchant.id
      && item.type.toLowerCase() !== "recurring";
    }, [])
    [0];

    if(transaction !== undefined) {
      acc.push({
        cofMechant: cofMechant,
        merchant: cofMechant.merchant,
        transaction: transaction,
        recurringDateOffset: transaction.purchaseDate,
      });
    }
    return acc;
  }, [])
  .sort((a, b) => {
    if(a.recurringDateOffset.isBefore(b.recurringDateOffset)) { return 1; }
    else if(a.recurringDateOffset.isAfter(b.recurringDateOffset)) { return -1; }
    else { return 0; }
  })

  const items = merchantsWithTransaction
  .map(merchantsWithTransactionObj => {
    const merchant = merchantsWithTransactionObj.merchant;
    let showGreenStatus = false;
    if(merchant) {
      showGreenStatus = merchantsWithTransactionObj.cofMechant.status === "updated" || merchantsWithTransactionObj.cofMechant.status === "card added";
    }
    
    return (
      <PaymentView
        key={ `Payment_${ merchant.id }` }
        dataPoint={ `Card_Merchant_${ merchant.id }` }
        billerName={ merchant.name }
        leftIcon={ merchant.logo }
        date={ merchantsWithTransactionObj.transaction.purchaseDate.format("M d, Y") }
        amount={ merchantsWithTransactionObj.transaction.amount.format() }
        className={ hasAddToMerchant ? "show-status" : "" } // branding option will modify this to amount or a status
        statusText={ merchantsWithTransactionObj.cofMechant ? merchantsWithTransactionObj.cofMechant.status : "" }
        statusClassName={ showGreenStatus ? "green-status" : "" }
        onClick={ paymentClick(merchant, merchantsWithTransactionObj.cofMechant) }
      />
    );
  });

  // Create PaymentView components for all transactions
  // To show a status in the PaymentView instead of an amount pass the class "show-status" to the className Property
  // To set the status text use the statusText property
  // To show a green status in the PaymentView pass the class "green-status" to the statusClassName Property
  /*const items = merchantsWithTransaction.map(merchantObj => {
    const cardOnFileMerchant = data.cardOnFileMerchants.filter(cardOnFileMerchant => {
      const card = cardOnFileMerchant.card ?? {};
      return cardID === card.id
      && (cardOnFileMerchant.merchant.id === merchantObj.transaction.merchant.id);
    })[0];
    let showGreenStatus = false;
    if(cardOnFileMerchant) {
      showGreenStatus = cardOnFileMerchant.status === "updated" || cardOnFileMerchant.status === "card added";
    }

    return (
      <PaymentView
        key={ `Payment_${ merchantObj.transaction.id }` }
        dataPoint={ `Card_Merchant_${ merchantObj.transaction.id }` }
        billerName={ merchantObj.transaction.merchant.name }
        leftIcon={ merchantObj.transaction.merchant.logo }
        date={ merchantObj.transaction.purchaseDate.format("M d, Y") }
        amount={ merchantObj.transaction.amount.format() }
        className={ hasAddToMerchant ? "show-status" : ""} // branding option will modify this to amount or a status
        statusText={ cardOnFileMerchant ? cardOnFileMerchant.status : "" }
        statusClassName={ showGreenStatus ? "green-status" : "" }
        onClick={ paymentClick(merchantObj.transaction.merchant, cardOnFileMerchant) } />
    );
  }, []);*/

  return (
    <div className="card-on-file-view">
      {/* This text shows when the branding option Add To Merchant is turned OFF. */}
      <Branding id="push_to_merchant_chw23" value="false">
        <p className="card-on-file-view-text">The Card on File list helps you keep tabs of merchants
        where you have stored your card on their file for convenient one-time or ad-hoc future 
        purchases.<SVGAsset id="QuestionMarkCircleIcon" /></p>
      </Branding>
      
      {/* This text shows when the branding option Add To Merchant is turned ON. */}
      <Branding id="push_to_merchant_chw23" value="true">
        <p className="card-on-file-view-text">The Card on File list helps you keep tabs
        of all the merchants where you have stored your card on their file for convenient one-time
        payments, recurring charges or ad-hoc future purchases.<SVGAsset id="QuestionMarkCircleIcon" /></p>
      </Branding>

      {/* This alert info box shows when the branding option Add To Merchant is turned ON. */}
      <Branding id="push_to_merchant_chw23" value="true">
        { ui.showEasilyUpdateMerchantsBanner && 
        <div className="alert-info-box grey-section">
          <div className="flex-container flex-horizontal align-center-container">
            <SVGAsset id="InformationIcon" className="set-width left-inline-icon" />
            <p>Now easily update eligible merchants with your new card details, on our site.
              Choose the merchant, securely verify their credentials, and we'll handle the rest!</p>
            <SVGAsset id="XIcon" className="set-width right-inline-icon clickable align-self-start x-icon" onClick={ toggleUIBanner("showEasilyUpdateMerchantsBanner") } />
          </div>
        </div>
        }
      </Branding>
      { items }
    </div>
  );
}